import { ContentLink, LoadStatus } from '@servicetitan/cp-ui';
import { Banner, BodyText, Button, Divider, Form, Stack } from '@servicetitan/design-system';
import { InputFieldState } from '@servicetitan/form-state';
import { provide, useDependencies } from '@servicetitan/react-ioc';
import { SettingsApi } from 'api';
import { observer } from 'mobx-react';
import { KeyboardEventHandler, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SpecialSettingsStore } from '../../../common/stores/special-settings.store';
import { TermsOfServiceLink } from '../../../legal-agreement/components/terms-of-service-link';
import { AuthLoginStore } from '../../stores/login.store';
import { LoginResult, PortalAuthStore } from '../../stores/portal-auth.store';
import { SettingsStore } from '../../stores/settings.store';
import { ContactInfoMode } from '../../utils/contact-info.validator';
import { enableAutoValidationOnValidationError } from '../../utils/field-auto-validate-helper';
import { handleEmailSpaceKeyDown } from '../../utils/field-email-helper';
import {
    useAuthCodeParams,
    useAuthHistory,
    useAuthLoginQueryParams,
} from '../../utils/use-auth-history';
import { GoogleLogin } from '../google';
import { AuthLayout } from '../layout/auth.layout';
import { AuthHeadline } from '../layout/components';
// import { MicrosoftLogin } from '../microsoft';
import { InfoTipLabel } from '../ui/info';
import { PasswordInput } from '../ui/password.input';

interface ModePresentation {
    label: string;
    inputType: 'email' | 'tel' | 'text';
    autoComplete: string;
    /** Returns a space-key handler when the mode needs to block spaces, or undefined. */
    spaceHandler: ((field: InputFieldState<string>) => KeyboardEventHandler) | undefined;
    buttonText: string;
    credentialsBannerText: string;
}

/** Exported for testing. Single mode→presentation map (ARCH-SCM-LOGIN-001). */
export const modePresentation: Record<ContactInfoMode, ModePresentation> = {
    'email-only': {
        label: 'Email',
        inputType: 'email',
        autoComplete: 'email',
        spaceHandler: handleEmailSpaceKeyDown,
        buttonText: 'Sign in with Email',
        credentialsBannerText: 'Invalid email or password',
    },
    'phone-only': {
        label: 'Phone number',
        inputType: 'tel',
        autoComplete: 'tel',
        spaceHandler: undefined,
        buttonText: 'Sign in with Phone',
        credentialsBannerText: 'Invalid phone number or password',
    },
    'either': {
        label: 'Email or phone number',
        inputType: 'text',
        autoComplete: 'username',
        spaceHandler: undefined,
        buttonText: 'Sign in',
        credentialsBannerText: 'Invalid email, phone number, or password',
    },
};

export const AuthLoginProvider = provide({
    singletons: [AuthLoginStore, SettingsStore, SettingsApi, SpecialSettingsStore],
})(({ children }) => children);

export const AuthLoginComponent = observer(() => {
    const [
        { canSubmit, form, init, submit, submitResult, submitStatus },
        { contactInfoMode, failedToLoadSettings, loadingSettings, permissionSettings },
        {
            mfaCodeLength,
            mfaCodeLifetimeMinutes,
            mfaContactMethod,
            mfaResendCooldownSeconds,
            mfaSessionToken,
        },
    ] = useDependencies(AuthLoginStore, SettingsStore, PortalAuthStore);

    const presentation = modePresentation[contactInfoMode];
    const { email, keepSigned, password } = form.$;

    const history = useAuthHistory();
    const rootNavigate = useNavigate();
    const params = useAuthLoginQueryParams();
    const { code } = useAuthCodeParams();
    const isSubmitting = submitStatus === LoadStatus.Loading;
    const displayRegisterLink = loadingSettings
        ? false
        : failedToLoadSettings || !permissionSettings?.requireInviteToSignUp;

    useEffect(() => {
        init(code);
    }, [init, code]);

    useEffect(() => {
        if (submitStatus === LoadStatus.Ok) {
            if (submitResult === LoginResult.MfaRequired && mfaSessionToken) {
                rootNavigate(
                    history.href('mfa', undefined, { keepSigned: String(form.$.keepSigned.value) }),
                    {
                        replace: true,
                        state: {
                            mfaSessionToken,
                            mfaCodeLength,
                            mfaCodeLifetimeMinutes,
                            mfaResendCooldownSeconds,
                            mfaContactMethod,
                        },
                    }
                );
            } else if (code) {
                history.replace('invite', { code }, { auto: true });
            } else {
                rootNavigate(params.redirect ? decodeURI(params.redirect) : '/');
            }
        }
    }, [
        submitStatus,
        submitResult,
        mfaSessionToken,
        mfaCodeLength,
        mfaCodeLifetimeMinutes,
        mfaResendCooldownSeconds,
        mfaContactMethod,
        rootNavigate,
        history,
        params.redirect,
        code,
        form.$.keepSigned.value,
    ]);

    return (
        <AuthLayout>
            {submitStatus === LoadStatus.Error ? (
                <Banner
                    status="warning"
                    icon
                    title={
                        submitResult === LoginResult.Restricted
                            ? 'You do not have an account with us. Please, contact support for more information.'
                            : submitResult === LoginResult.RateLimited
                              ? 'Too many attempts. Please wait a moment before trying again.'
                              : presentation.credentialsBannerText
                    }
                    className="m-b-2"
                />
            ) : params.info === 'ok' ? (
                <Banner status="success" icon title="Successful registration" className="m-b-2">
                    Now you can sign in with your credentials
                </Banner>
            ) : params.info === 'exists' ? (
                <Banner
                    status="info"
                    icon
                    title="It looks like you’re registered. Go ahead and sign in."
                    className="m-b-2"
                />
            ) : null}

            <AuthHeadline title="Sign In" />

            {contactInfoMode !== 'phone-only' && (
                <Stack direction="column" className="m-b-3" spacing={2}>
                    <GoogleLogin />
                    {/* <MicrosoftLogin /> */}
                    <Divider className="m-t-2">
                        <BodyText subdued size="small" className="m-x-1">
                            or log in to continue
                        </BodyText>
                    </Divider>
                </Stack>
            )}

            <Form onSubmit={submit}>
                <Form.Input
                    label={presentation.label}
                    className="m-t-2"
                    value={email.value}
                    onChange={email.onChangeHandler}
                    onKeyDown={presentation.spaceHandler?.(email)}
                    onBlur={enableAutoValidationOnValidationError(email)}
                    error={email.error}
                    disabled={isSubmitting}
                    autoComplete={presentation.autoComplete}
                    type={presentation.inputType}
                />

                <PasswordInput
                    label="Password"
                    value={password.value}
                    onChange={password.onChangeHandler}
                    onBlur={enableAutoValidationOnValidationError(password)}
                    error={password.error}
                    disabled={isSubmitting}
                />
                <Form.Checkbox
                    checked={keepSigned.value}
                    onChange={keepSigned.onChangeHandler}
                    disabled={isSubmitting}
                    label={
                        <InfoTipLabel label="Keep me signed in">
                            Checking this box will reduce the number of times you’re asked to sign
                            in. To keep your account secure, use this option only on your personal
                            devices.
                        </InfoTipLabel>
                    }
                />

                <Button
                    type="submit"
                    width="full"
                    primary
                    loading={isSubmitting}
                    disabled={!canSubmit}
                >
                    {presentation.buttonText}
                </Button>

                <BodyText className="ta-center m-t-2 c-neutral-200" size="small">
                    By signing in you agree to the{' '}
                    <TermsOfServiceLink className="c-neutral-200">
                        Terms of Service
                    </TermsOfServiceLink>
                    .
                </BodyText>

                <div className="ta-center m-t-3">
                    <ContentLink href={history.href('recovery')} primary>
                        Can't sign in?
                    </ContentLink>
                </div>

                {displayRegisterLink && (
                    <BodyText className="ta-center m-t-2 ">
                        Don't have an account?{' '}
                        <ContentLink href={history.href('register')} primary>
                            Register
                        </ContentLink>
                    </BodyText>
                )}
            </Form>
        </AuthLayout>
    );
});

export const AuthLogin = () => (
    <AuthLoginProvider>
        <AuthLoginComponent />
    </AuthLoginProvider>
);
